/**
 * "Submitted Appeals" sub-tab of the Deliveries view.
 *
 * Lists delivery appeals with quick-isolate tabs (All / Action Required /
 * Resolved), per-tab counts, filters, pagination and CSV export. Extracted
 * verbatim from DeliveryTab.tsx to keep that file under the 500-line limit.
 *
 * @link DeliveryTab for the parent that mounts this and wires View / Respond.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Loader2, Package, Download, ListFilter } from 'lucide-react';
import { purchasingAPI, DeliveryAppeal } from '../../../lib/api';
import { toast } from 'sonner';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../filters/FilterSection';
import { Pagination } from '../../ui/pagination';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { csvBlob } from '../../../utils/fileDownload';
import { useAppealBulkRespond } from './delivery/useAppealBulkRespond';
import AppealBulkBar from './delivery/AppealBulkBar';
import { AppealSelectHeader } from './delivery/AppealSelectCells';
import { useAuth } from '../../../contexts/AuthContext';
import { useViewKey } from '../../../contexts/ViewContext';
import { useIsSupplier } from '../../../hooks/usePortalMode';
import SearchableSelect from '../../ui/SearchableSelect';
import {
  type AppealColumnConfig, loadAppealColumns, AppealsColumnsDropdown, AppealRow, filterCsvByVisibleColumns,
  type AppealFilters, EMPTY_APPEAL_FILTERS, buildAppealFilterParams, actionRequiredParams, useDebouncedAppealFilters, AppealsEmptyState,
} from './appeals';

/** A Supplier dropdown option: the supplier company id and its name. */
interface SupplierOption {
  value: string;
  label: string;
}

// Stable default so the stale-selection effect below doesn't re-run every render.
const NO_SUPPLIER_OPTIONS: SupplierOption[] = [];

interface AppealsSubTabProps {
  formatDate: (dateString: string | undefined) => string;
  getAppealStatusBadge: (status: string, isCustomerAppeal?: boolean, workflowStatus?: string) => ReactNode;
  onViewGrn: (grnId: number) => void;
  onRespondAppeal: (appealId: number) => void;
  // Bump this counter from the parent (e.g. after an Accept/Reject/Query in
  // AppealResponseModal closes) to force the appeals list and tab counts to
  // refetch — without this, accepted rows lingered with the stale
  // "awaiting_response" badge and Respond button until a hard refresh.
  refreshKey?: number;
  // Customer bulk (multi-select) accept/reject of supplier appeals. Enabled by
  // the parent for customer-side viewers when the platform toggle is on.
  allowBulkRespond?: boolean;
  // CR-7169: customer-side viewer — only they get the row actions. Separate
  // from allowBulkRespond: the row actions don't depend on the bulk toggle.
  canRespond?: boolean;
  // CR-7167: Initial tab selection for deep-linking from dashboard.
  // 'action_required' pre-selects the Action Required tab.
  initialTab?: 'all' | 'action_required' | 'resolved';
  /** CR-8187: the Deliveries tab's supplier list, so both Supplier filters offer the same suppliers. */
  supplierOptions?: SupplierOption[];
}

export default function AppealsSubTab({ formatDate, getAppealStatusBadge, onViewGrn, onRespondAppeal, refreshKey = 0, allowBulkRespond = false, canRespond = false, initialTab = 'all', supplierOptions = NO_SUPPLIER_OPTIONS }: AppealsSubTabProps) {
  const { user } = useAuth();
  const userId = user?.id;
  // CR-8187: the site switcher only changes viewKey, it doesn't remount this tab.
  const viewKey = useViewKey();
  const isSupplierView = useIsSupplier();
  // CR-7168: Column configuration - persisted per user
  const [columns, setColumns] = useState<AppealColumnConfig[]>(() => loadAppealColumns(userId));
  // CR-7168: Visible columns in user-defined order + dynamic min-width (+ 200px for View/Action)
  const visibleCols = columns.filter((c) => c.visible);
  const tableMinWidth = visibleCols.reduce((sum, c) => sum + parseInt(c.minWidth, 10), 200);

  const [appeals, setAppeals] = useState<DeliveryAppeal[]>([]);
  const [appealsLoading, setAppealsLoading] = useState(true);
  const [appealsError, setAppealsError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  // CR-4736: Proper pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // CR-4736 rework: Default to 25 to align with pagination display
  const [appealPageSize, setAppealPageSize] = useState(25);
  const [appealFilters, setAppealFilters] = useState<AppealFilters>(EMPTY_APPEAL_FILTERS);
  // CR-4786: tab bar above the table — gives customers a one-click route to
  // appeals awaiting their response, matching the Exceptions-style pattern
  // used on PO Lines. Ticket called out manual scanning as the pain point.
  type AppealTab = 'all' | 'action_required' | 'resolved';
  // CR-7167: initialTab prop allows deep-linking from dashboard to Action Required.
  const [appealTab, setAppealTab] = useState<AppealTab>(initialTab);
  // CR-4651 rework: per-tab counts shown in the labels in brackets, like the
  // PO Lines tabs. Recomputed whenever the user's filter set changes.
  const [appealTabCounts, setAppealTabCounts] = useState<Record<AppealTab, number>>({
    all: 0,
    action_required: 0,
    resolved: 0,
  });
  // Local refetch trigger — bumped after a bulk accept/reject so the list and
  // tab counts refresh without a parent round-trip.
  const [bulkRefresh, setBulkRefresh] = useState(0);
  const bulk = useAppealBulkRespond(appeals, allowBulkRespond);
  // CR-8206: requests use the debounced copy; the inputs keep `appealFilters`.
  const queryFilters = useDebouncedAppealFilters(appealFilters);
  // Status only narrows the All tab (CR-4786), so on the other tabs it doesn't count.
  const filtersActive = Object.keys(buildAppealFilterParams(queryFilters)).length > 0
    || (appealTab === 'all' && Boolean(queryFilters.status));

  // CR-8187: drop a supplier the scope no longer offers (a site switch reloads
  // supplierOptions) or supplier view can't see, rather than filter by it unseen.
  useEffect(() => {
    const offered = !isSupplierView && supplierOptions.some((o) => o.value === appealFilters.supplier);
    if (appealFilters.supplier && !offered) setAppealFilters((prev) => ({ ...prev, supplier: '' }));
  }, [supplierOptions, isSupplierView, appealFilters.supplier]);

  // CR-4736: back to page 1 when filters, tab or scope change. CR-8187: before
  // fetching — the old page under narrower filters 404s ("Invalid page").
  const filterKey = JSON.stringify([queryFilters, appealTab, viewKey]);
  const lastFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKeyRef.current !== filterKey) {
      lastFilterKeyRef.current = filterKey;
      if (currentPage !== 1) { setCurrentPage(1); return; } // that change refetches
    }
    fetchAppeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, currentPage, appealPageSize, refreshKey, bulkRefresh]);

  // CR-4651 rework P6a: fetch the count for each sub-tab so the labels can
  // show "(N)" without the user having to click into each tab. Only refires
  // when filters change (not on tab switch or pagination) since the counts
  // are filter-scoped, not tab-scoped.
  // refreshKey: also re-runs after the parent forces a refresh (e.g. after
  // accepting an appeal) so the "(N)" tab counts move alongside the row.
  useEffect(() => {
    fetchTabCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilters, refreshKey, bulkRefresh, viewKey]);

  // Maps the active quick-tab to its status query params — shared by the list
  // fetch and the CSV export so both always show the same rows. The tab wins
  // over the status dropdown (CR-4786); the dropdown only applies in All.
  const tabStatusParams = (): Record<string, string> =>
    appealTab === 'action_required' ? actionRequiredParams(isSupplierView) // CR-8239: per side
      : appealTab === 'resolved' ? { status__in: 'accepted,rejected' }
        : queryFilters.status ? { status: queryFilters.status }
          : {};

  // CR-8187: each keystroke or scope switch refetches; only the newest
  // response may land, or a slow older one overwrites the current rows.
  const listRequestRef = useRef(0);
  const countsRequestRef = useRef(0);
  // Full-card spinner only until the first response — after that the panel
  // stays mounted so a text filter keeps focus while the user types.
  const loadedOnceRef = useRef(false);

  const fetchAppeals = async () => {
    const requestId = ++listRequestRef.current;
    const isLatest = () => requestId === listRequestRef.current;
    try {
      setAppealsLoading(true);
      setAppealsError(null);
      const params: Record<string, string | number> = {
        ordering: '-created_at',
        page_size: appealPageSize,
        page: currentPage,
      };
      Object.assign(params, tabStatusParams(), buildAppealFilterParams(queryFilters));
      const response = await purchasingAPI.getDeliveryAppeals(params);
      if (!isLatest()) return;
      const data = response.data;
      if (Array.isArray(data)) {
        setAppeals(data);
        setTotalCount(data.length);
      } else if (data && typeof data === 'object' && 'results' in data) {
        const paginated = data as { results: DeliveryAppeal[]; count: number };
        setAppeals(paginated.results);
        setTotalCount(paginated.count);
      }
    } catch (err: unknown) {
      if (!isLatest()) return;
      console.error('Error fetching appeals:', err);
      setAppealsError(getApiErrorMessage(err, 'Failed to load appeals'));
    } finally {
      if (isLatest()) {
        loadedOnceRef.current = true;
        setAppealsLoading(false);
      }
    }
  };

  const clearAppealFilters = () => {
    setAppealFilters(EMPTY_APPEAL_FILTERS);
  };

  // CR-4651 rework P6a: cheap (page_size=1) parallel calls to read the
  // .count off each tab's filtered queryset.
  const fetchTabCounts = async () => {
    const requestId = ++countsRequestRef.current;
    const baseFilters = buildAppealFilterParams(queryFilters);

    const extractCount = (data: unknown): number => {
      if (Array.isArray(data)) return data.length;
      if (data && typeof data === 'object' && 'count' in data) {
        return (data as { count: number }).count;
      }
      return 0;
    };

    try {
      const [allRes, actionRes, resolvedRes] = await Promise.all([
        purchasingAPI.getDeliveryAppeals({ ...baseFilters, page_size: 1, page: 1 }),
        purchasingAPI.getDeliveryAppeals({
          ...baseFilters, ...actionRequiredParams(isSupplierView),
          page_size: 1, page: 1,
        }),
        purchasingAPI.getDeliveryAppeals({
          ...baseFilters, status__in: 'accepted,rejected', page_size: 1, page: 1,
        }),
      ]);
      if (requestId !== countsRequestRef.current) return;
      setAppealTabCounts({
        all: extractCount(allRes.data),
        action_required: extractCount(actionRes.data),
        resolved: extractCount(resolvedRes.data),
      });
    } catch (err) {
      console.error('Failed to fetch appeal tab counts:', err);
    }
  };

  const [exporting, setExporting] = useState(false);
  const handleExportAppeals = async () => {
    try {
      setExporting(true);
      const params: Record<string, string | number> = {};
      // Same tab-aware status params as the list, so the export matches exactly
      // the rows the user is looking at.
      Object.assign(params, tabStatusParams(), buildAppealFilterParams(queryFilters));
      const response = await purchasingAPI.exportDeliveryAppeals(params);
      // CR-7168: Filter and reorder CSV columns based on user's visible column config
      const rawCsv = await response.data.text();
      const filteredCsv = filterCsvByVisibleColumns(rawCsv, columns);
      // CR-8703: reading the file as text drops the server's BOM; put it back.
      const blob = csvBlob(filteredCsv);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appeals_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Appeals exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export appeals');
    } finally {
      setExporting(false);
    }
  };

  /** Free-text appeal filter — same input element and handler as before. */
  const appealTextField = (label: string, key: 'grn_number' | 'po_number' | 'part_number') => (
    <FilterField label={label}>
      <input
        type="text"
        value={appealFilters[key]}
        onChange={(e) => setAppealFilters({ ...appealFilters, [key]: e.target.value })}
        placeholder="Search..."
        className={filterControlClass}
      />
    </FilterField>
  );

  if (appealsLoading && !loadedOnceRef.current) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-600" />
            Submitted Appeals
            <span className="text-sm text-muted-foreground font-normal">({totalCount})</span>
            {appealsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Refreshing appeals" />}
          </CardTitle>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            {/* CR-7168: Column configuration dropdown */}
            <AppealsColumnsDropdown columns={columns} onChange={setColumns} userId={userId} />
            <button
              onClick={handleExportAppeals}
              disabled={exporting}
              className="px-3 py-1.5 text-sm rounded-lg border hover:bg-muted transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export
            </button>
          </div>
        </div>
        {/* Grouped with the app-wide `filters/FilterSection` primitives so this
            panel reads like Deliveries/ASN. `bare` — CardHeader already pads. */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t space-y-4">
            <FilterSection title="Appeal" icon={ListFilter} bare>
              <FilterGrid>
                <FilterField label="Status">
                  <select
                    value={appealFilters.status}
                    onChange={(e) => setAppealFilters({ ...appealFilters, status: e.target.value })}
                    className={filterControlClass}
                  >
                    <option value="">All</option>
                    <option value="awaiting_response">Awaiting Response</option>
                    <option value="accepted">Accepted</option>
                    <option value="queried">Queried</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </FilterField>
              </FilterGrid>
            </FilterSection>

            {/* Order Details — mirrors the section of the same name on Deliveries. */}
            <FilterSection title="Order Details" icon={Package} bare>
              <FilterGrid>
                {appealTextField('GRN Number', 'grn_number')}
                {appealTextField('PO Number', 'po_number')}
                {appealTextField('Part Number', 'part_number')}
                {/* CR-8187: customers only — a supplier only ever sees its own appeals. */}
                {!isSupplierView && (
                  <FilterField label="Supplier">
                    <SearchableSelect
                      options={supplierOptions}
                      value={appealFilters.supplier}
                      onChange={(val) => setAppealFilters({ ...appealFilters, supplier: val })}
                      placeholder="All Suppliers"
                    />
                  </FilterField>
                )}
              </FilterGrid>
            </FilterSection>

            <div className="flex justify-end">
              <button onClick={clearAppealFilters} className="text-xs text-muted-foreground hover:text-foreground underline">
                Clear All
              </button>
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {/* CR-4786: quick-isolate tabs — Action Required surfaces supplier
            appeals awaiting the customer's response so they don't have to
            scan the full list. Resolved hides handled items. */}
        <div className="px-4 pt-2">
          <div className="inline-flex rounded-lg border bg-muted/30 p-1 gap-1">
            {(
              [
                { key: 'all', label: 'All' },
                { key: 'action_required', label: 'Action Required' },
                { key: 'resolved', label: 'Resolved' },
              ] as Array<{ key: AppealTab; label: string }>
            ).map((t) => {
              const isActive = appealTab === t.key;
              const count = appealTabCounts[t.key];
              // CR-4651 rework P6b: highlight outstanding work — the
              // Action Required count goes bold + red when > 0 so it
              // stands out without the user clicking into the tab.
              const isUrgent = t.key === 'action_required' && count > 0;
              return (
                <button
                  key={t.key}
                  onClick={() => setAppealTab(t.key)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                  }`}
                >
                  {t.label}{' '}
                  <span
                    className={
                      isUrgent && !isActive
                        ? 'font-bold text-red-600'
                        : isActive
                          ? 'opacity-90'
                          : 'text-muted-foreground'
                    }
                  >
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {/* CR-8206: errors and the empty state render inside the card, so the
            filter panel above stays put. */}
        {appealsError ? (
          <div className="m-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {appealsError}</p>
            <button onClick={fetchAppeals} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
              Try again
            </button>
          </div>
        ) : appeals.length === 0 ? (
          <AppealsEmptyState filtered={filtersActive} onClear={clearAppealFilters} />
        ) : (
          <>
            {/* Customer bulk (multi-select) accept/reject action bar. */}
            {bulk.enabled && (
              <AppealBulkBar bulk={bulk} onDone={() => { bulk.clear(); bulk.setBulkAction(null); setBulkRefresh((k) => k + 1); }} />
            )}
            {/* CR-4736: Top Pagination */}
            <div className="px-4">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / appealPageSize)}
                totalCount={totalCount}
                itemsPerPage={appealPageSize}
                onPageChange={(page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                onPageSizeChange={(size) => { setAppealPageSize(size); setCurrentPage(1); }}
                itemLabel="appeals"
              />
            </div>
          {/* CR-7168: Dynamic min-width based on visible columns */}
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
            <table className="w-full text-sm" style={{ minWidth: `${tableMinWidth}px` }}>
              <thead className="sticky top-0 z-20">
                <tr className="border-b bg-muted/80 backdrop-blur-sm">
                  {bulk.enabled && <AppealSelectHeader bulk={bulk} />}
                  {/* CR-7168: Headers rendered in user-defined order */}
                  {visibleCols.map((col) => (
                    <th key={col.id} className="text-left py-3 px-4 font-medium" style={{ minWidth: col.minWidth }}>
                      {col.label}
                    </th>
                  ))}
                  {/* View + Action columns are always visible and pinned right */}
                  <th className="text-left py-3 px-4 font-medium sticky right-[100px] bg-muted/95 backdrop-blur-sm z-30 min-w-[100px]">View</th>
                  <th className="text-left py-3 px-4 font-medium sticky right-0 bg-muted/95 backdrop-blur-sm z-30 min-w-[100px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {appeals.map((appeal) => (
                  <AppealRow
                    key={appeal.id}
                    appeal={appeal}
                    visibleColumns={visibleCols}
                    formatDate={formatDate}
                    getAppealStatusBadge={getAppealStatusBadge}
                    onViewGrn={onViewGrn}
                    onRespondAppeal={onRespondAppeal}
                    bulk={bulk}
                    canRespond={canRespond}
                    onResponded={() => setBulkRefresh((k) => k + 1)}
                  />
                ))}
              </tbody>
            </table>
          </div>
            {/* CR-4736: Bottom Pagination */}
            <div className="px-4">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / appealPageSize)}
                totalCount={totalCount}
                itemsPerPage={appealPageSize}
                onPageChange={(page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                onPageSizeChange={(size) => { setAppealPageSize(size); setCurrentPage(1); }}
                itemLabel="appeals"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
